import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import QrScanner from 'qr-scanner';
import { API_BASE_URL } from '../config';
import { toast } from 'react-toastify';
import { 
  Basketball, 
  Bicycle,
  PersonSimpleRun,
  Barbell,
  Football,
  SoccerBall,
  Baseball,
  Volleyball,
  TennisBall,
  Timer,
  Medal,
  Star,
  TShirt,
  Horse,
  Strategy,
  MapTrifold,
  List
} from 'phosphor-react';
import ReactConfetti from 'react-confetti';
import TutorialOverlay from './TutorialOverlay';
import Plan from './Plan';
import '../styles/Map.css';
import TaskIcon from './TaskIcon';


const Tasks = ({ username }) => {
  const [tasks, setTasks] = useState([]);
  const [refreshKey, setRefreshKey] = useState(0);
  const [isScanning, setIsScanning] = useState(false);
  const [currentTaskId, setCurrentTaskId] = useState(null);
  const [showConfetti, setShowConfetti] = useState(false);
  const [completedTaskId, setCompletedTaskId] = useState(null);
  const [isGloballyLocked, setIsGloballyLocked] = useState(false);
  const [viewMode, setViewMode] = useState('list');
  const videoRef = useRef(null);
  const scannerRef = useRef(null);

  const checkLockStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/lock-status`);
      setIsGloballyLocked(response.data.tasksLocked);
    } catch (error) {
      console.error('Error checking lock status:', error);
    }
  }, []);

  const fetchTasks = useCallback(async () => {
    try {

      const [tasksResponse, lockResponse] = await Promise.all([
        axios.get(`${API_BASE_URL}/tasks`, { params: { username } }),
        axios.get(`${API_BASE_URL}/lock-status`)
      ]);
      setTasks(tasksResponse.data);
      setIsGloballyLocked(lockResponse.data.tasksLocked);

    } catch (error) {
      console.error('Error fetching tasks:', error);
    }
  }, [username]);

  useEffect(() => {
    fetchTasks();
    checkLockStatus();
    const taskInterval = setInterval(fetchTasks, 5000);
    const lockInterval = setInterval(checkLockStatus, 1000);
    
    return () => {
      clearInterval(taskInterval);
      clearInterval(lockInterval);
    };
  }, [fetchTasks, checkLockStatus]);

  const getAvailabilityText = (task) => {
    if (isGloballyLocked) {
      return "Zadanie zablokowane przez administratora";
    }
    if (task.tasksLocked) {
      return "Zadanie zablokowane przez administratora";
    }
    if (task.minutes_remaining > 0) {
      return `Można wykonać ponownie za ${task.minutes_remaining} min`;
    }
    if (task.completed_count && task.max_count) {
      return `${task.completed_count} / ${task.max_count}`;
    }
    return "Można wykonać teraz";
  };

  const isTaskLocked = (task) => {
    return task.tasksLocked || task.minutes_remaining > 0;
  };

  const startScanning = (taskId) => {
    setCurrentTaskId(taskId);
    setIsScanning(true);
  };

  const handleQrScan = (scannedCode) => {
    if (scannedCode && currentTaskId) {
      scannerRef.current?.destroy();
      setIsScanning(false);
      
      axios.post(`${API_BASE_URL}/complete-task`, {
        username,
        taskId: currentTaskId,
        scannedCode: scannedCode.trim()
      })
      .then(response => {
        if (response.data.error) {
          toast.error(
            response.data.error === 'Invalid QR code' 
              ? 'Nieprawidłowy kod QR dla tego zadania!' 
              : 'Wystąpił błąd podczas weryfikacji kodu QR'
          );
        } else {
          setCompletedTaskId(currentTaskId);
          setShowConfetti(true);
          setTimeout(() => {
            setShowConfetti(false);
            setCompletedTaskId(null);
          }, 3000);
          toast.success('Zadanie wykonane pomyślnie!');
          fetchTasks();
        }
      })
      .catch(error => {
        console.error('Błąd wykonania zadania:', error);
        const errorMessage = error.response?.data?.error || 'Błąd podczas wykonywania zadania';
        toast.error(errorMessage);
      });
    }
  };

  useEffect(() => {
    if (isScanning && videoRef.current) {
      scannerRef.current = new QrScanner(
        videoRef.current,
        result => {
          handleQrScan(result.data);  // Update this reference
        },
        {
          preferredCamera: 'environment',
          highlightScanRegion: true,
        }
      );
      
      scannerRef.current.start();
    }

    return () => {
      if (scannerRef.current) {
        scannerRef.current.destroy();
        scannerRef.current = null;
      }
    };
  }, [isScanning]);

  const stopScanning = () => {
    if (scannerRef.current) {
      scannerRef.current.destroy();
      scannerRef.current = null;
    }
    setIsScanning(false);
    setCurrentTaskId(null);
  };

  const getCurrentTask = () => {
    return tasks.find(task => task.id === currentTaskId);
  };

  return (
    <div className="max-w-2xl mx-auto space-y-4 px-4">
      {showConfetti && (
        <ReactConfetti
          width={window.innerWidth}
          height={window.innerHeight}
          recycle={false}
          numberOfPieces={200}
        />
      )}

      {isGloballyLocked && (
        <div className="bg-red-900/50 border border-red-700 text-red-200 px-4 py-3 rounded relative mb-4">
          <strong className="font-bold">Uwaga! </strong>
          <span className="block sm:inline">Zadania są tymczasowo zablokowane przez administratora.</span>
        </div>
      )}

      <div className="sticky top-0 z-20 bg-gray-900 pt-4 pb-2 mb-4">
        <div className="flex justify-center gap-2">
          <button
            onClick={() => setViewMode('list')}
            className={`flex items-center px-4 py-2 rounded-lg ${
              viewMode === 'list' ? 'bg-blue-600 text-white' : 'bg-gray-700 text-gray-300'
            }`}
          >
            <List className="mr-2" size={20} />
            Lista
          </button>
          <button
            onClick={() => setViewMode('map')}
            className={`flex items-center px-4 py-2 rounded-lg ${
              viewMode === 'map' ? 'bg-blue-600 text-white' : 'bg-gray-700 text-gray-300'
            }`}
          >
            <MapTrifold className="mr-2" size={20} />
            Mapa
          </button>
        </div>
      </div>

      {isScanning && (
        <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50">
          <div className="bg-gray-800 p-4 rounded-lg max-w-sm w-full mx-4">
            {getCurrentTask() && (
              <div className="mb-4 text-center">
                <h3 className="text-xl font-bold mb-2 text-white">{getCurrentTask().name}</h3>
                <div className="flex items-center justify-center gap-2 text-blue-400 mb-2">
                  <TaskIcon type={getCurrentTask().type} points={getCurrentTask().points} />
                  <span className="font-bold">{getCurrentTask().points} pkt</span>
                </div>
                <p className="text-sm text-gray-400">{getCurrentTask().location}</p>
              </div>
            )}
            <h3 className="text-lg font-bold mb-2 text-white">Zeskanuj kod QR</h3>
            <video ref={videoRef} className="w-full aspect-square object-cover rounded-lg" />
            <button
              onClick={stopScanning}
              className="mt-4 w-full bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700"
            >
              Anuluj
            </button>
          </div>
        </div>
      )}
      
      <div className={viewMode === 'map' ? 'bg-gray-900 rounded-lg p-4' : 'space-y-4'}>
        {viewMode === 'map' ? (
          <Plan 
            currentMap="garden"
            onTaskSelect={startScanning}
            isTaskLocked={isTaskLocked}
            isGloballyLocked={isGloballyLocked}
          />
        ) : (
          Array.isArray(tasks) && tasks.length > 0 ? (
            tasks
              .filter(task => !task.name.startsWith('UKRYJ'))
              .map(task => (
              <div key={task.id} 
                className={`bg-gray-800 rounded-lg p-4 shadow-md transition-all duration-300 ${
                  task.id === completedTaskId ? 'scale-105 border-2 border-green-500' : ''
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-4">
                    <div className="mt-1">
                      <TaskIcon type={task.type} points={task.points} />
                    </div>
                    <div className="flex flex-col">
                      <h3 className="text-xl font-bold text-white mb-1">{task.name}</h3>
                      <p className="text-sm text-gray-400">
                        {getAvailabilityText(task)}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => startScanning(task.id)}
                    disabled={isTaskLocked(task) || isGloballyLocked}
                    className={`px-4 py-2 rounded-full font-medium ${
                      isTaskLocked(task) || isGloballyLocked
                        ? 'text-gray-500 border-2 border-gray-600 cursor-not-allowed'
                        : 'text-blue-400 border-2 border-blue-500 hover:bg-blue-900/50'
                    }`}
                  >
                    {isGloballyLocked ? 'Niedostępne' : task.tasksLocked ? 'Zablokowane' : task.minutes_remaining > 0 ? 'Niedostępne' : 'Zeskanuj QR'}
                  </button>
                </div>
              </div>
            ))
          ) : (
            <div className="bg-gray-800 rounded-lg p-4 shadow-md text-center text-gray-400">
              Brak dostępnych zadań
            </div>
          )
        )}
      </div>
    </div>
  );
}

export default Tasks;